import React, { useMemo } from "react";
import { Warehouse, Building, StickyNote } from "lucide-react";
import { Link } from "react-router-dom";
import { RoleHelper } from "src/helpers";
import { ROUTES_CONFIG } from "src/constants/routesConfig";
import { POWarehouseInfo, Product, PurchaseOrder } from "src/types";
import { useAuthorisation } from "src/context";
import { LoadingState } from "src/components";
import { StatusPill } from "src/components/ui";
import { CollapsibleSection } from "src/components/ui/collapsible";
import { POCompletionStatus, POProductConfirmation } from "src/types/database/purchaseOrder";
import { PurchaseOrderService } from "src/services";
import { useChildPurchaseOrders } from "src/hooks";
import {
  CheckInHistoryList,
  CheckInProductReceiptsTable,
  CheckInFlaggedIssuesPanel
} from "src/components/sections/warehouseCheckIns";

export interface CheckInNote {
  note: string;
  createdAt: string;
  submittedByEmail?: string;
}

interface PurchaseOrderWarehouseSectionProps extends POWarehouseInfo {
  purchaseOrderId: string;
  products?: Product[];
  productConfirmations?: POProductConfirmation[];
  showProductReceipts?: boolean;
  childPurchaseOrderIds?: string[];
  purchaseOrderService: PurchaseOrderService;
  completionStatus?: string;
  checkInNotes?: CheckInNote[];
}

const CheckInNotesPanel: React.FC<{ notes: CheckInNote[] }> = ({ notes }) => {
  if (notes.length === 0) return null;

  return (
    <div className="space-y-2">
      <h4 className="text-sm font-semibold text-secondary-700 flex items-center">
        <StickyNote className="w-4 h-4 mr-2 text-amber-600" />
        Check-In Notes ({notes.length})
      </h4>
      <div className="space-y-2">
        {notes.map((entry, index) => (
          <div key={`${entry.createdAt}-${index}`} className="rounded-xl border border-amber-200 bg-amber-50 p-4">
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 mb-2">
              <span className="text-xs font-medium text-secondary-600">{new Date(entry.createdAt).toLocaleString()}</span>
              {entry.submittedByEmail && (
                <span className="text-xs text-muted-600 truncate">{entry.submittedByEmail}</span>
              )}
            </div>
            <p className="text-sm text-secondary-700 whitespace-pre-wrap break-words">{entry.note}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

interface SinglePOWarehouseContentProps {
  purchaseOrder: PurchaseOrder;
  isChild?: boolean;
  childReceivedQuantitiesMap?: Map<string, number>;
  hasChildPOs?: boolean;
}

const SinglePOWarehouseContent: React.FC<SinglePOWarehouseContentProps> = ({
  purchaseOrder,
  isChild = false,
  childReceivedQuantitiesMap,
  hasChildPOs = false
}) => {
  const { userRole } = useAuthorisation();
  const isHealfTeam = RoleHelper.isHealfTeam(userRole);
  const canViewWarehouseDetails = isHealfTeam || userRole === "WAREHOUSE";
  const isVendorUser = userRole === "VENDOR";

  const {
    asn,
    assignedWarehouseCompany,
    checkInStatus,
    checkInTimestamps,
    checkInFlaggedIssues,
    products = [],
    confirmationDetails
  } = purchaseOrder;

  const productConfirmationMap = useMemo(() => {
    const map = new Map<string, number>();
    (confirmationDetails?.products ?? []).forEach(({ productId, confirmedQty }) => {
      map.set(productId, confirmedQty);
    });
    return map;
  }, [confirmationDetails]);

  const statusPillLink =
    isHealfTeam && checkInFlaggedIssues?.length
      ? `${ROUTES_CONFIG.receivingDiscrepancies.path}?id=${purchaseOrder.id}`
      : undefined;

  const buildCheckInLink = canViewWarehouseDetails
    ? (timestamp: string) => `${ROUTES_CONFIG.warehouseCheckIns.path}?id=${purchaseOrder.id}&timestamp=${timestamp}`
    : undefined;

  if (isVendorUser) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {asn && (
            <div className="bg-secondary-50 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Warehouse className="w-4 h-4 text-primary-600" />
                <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">ASN</span>
              </div>
              <p className="text-sm font-medium text-secondary-900">{asn}</p>
            </div>
          )}
          {assignedWarehouseCompany && (
            <div className="bg-secondary-50 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Building className="w-4 h-4 text-primary-600" />
                <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">
                  Warehouse Company
                </span>
              </div>
              <p className="text-sm font-medium text-secondary-900">{assignedWarehouseCompany}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status Badge */}
      {checkInStatus && (
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-secondary-700">Check-In Status</span>
          <StatusPill status={checkInStatus} linkPath={statusPillLink} />
        </div>
      )}

      {/* ASN & Warehouse Company */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {asn && (
          <div className="bg-secondary-50 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Warehouse className="w-4 h-4 text-primary-600" />
              <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">ASN</span>
            </div>
            <p className="text-sm font-medium text-secondary-900">{asn}</p>
          </div>
        )}
        {assignedWarehouseCompany && (
          <div className="bg-secondary-50 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Building className="w-4 h-4 text-primary-600" />
              <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">
                Warehouse Company
              </span>
            </div>
            <p className="text-sm font-medium text-secondary-900">{assignedWarehouseCompany}</p>
          </div>
        )}
      </div>

      {checkInStatus && <CheckInHistoryList checkInTimestamps={checkInTimestamps} buildLink={buildCheckInLink} />}

      {products.length > 0 && (
        <CheckInProductReceiptsTable
          products={products}
          productConfirmationMap={productConfirmationMap}
          childReceivedQuantitiesMap={childReceivedQuantitiesMap}
          isChild={isChild}
          hasChildPOs={hasChildPOs}
          isVendorUser={isVendorUser}
          hasCheckIns={!!checkInTimestamps?.length}
        />
      )}

      <CheckInFlaggedIssuesPanel flaggedIssues={checkInFlaggedIssues} />
    </div>
  );
};

export const PurchaseOrderWarehouseSection: React.FC<PurchaseOrderWarehouseSectionProps> = ({
  asn,
  assignedWarehouseCompany,
  checkInStatus,
  checkInTimestamps,
  checkInFlaggedIssues,
  purchaseOrderId,
  products = [],
  productConfirmations = [],
  showProductReceipts = true,
  childPurchaseOrderIds,
  purchaseOrderService,
  completionStatus,
  checkInNotes = []
}) => {
  const { childPurchaseOrders, isLoading: isLoadingChildPOs } = useChildPurchaseOrders(
    childPurchaseOrderIds,
    purchaseOrderService
  );

  const childReceivedQuantitiesMap = useMemo(() => {
    const map = new Map<string, number>();
    childPurchaseOrders.forEach((childPO) => {
      childPO.products?.forEach((product) => {
        const currentSum = map.get(product.id) ?? 0;
        map.set(product.id, currentSum + (product.receivedQty ?? 0));
      });
    });
    return map;
  }, [childPurchaseOrders]);

  const hasChildPOs = !!childPurchaseOrderIds && childPurchaseOrderIds.length > 0;

  const parentPurchaseOrder: PurchaseOrder = useMemo(
    () =>
      ({
        id: purchaseOrderId,
        completionStatus: completionStatus as POCompletionStatus,
        asn,
        assignedWarehouseCompany,
        checkInStatus,
        checkInTimestamps,
        checkInFlaggedIssues,
        products,
        confirmationDetails: productConfirmations?.length ? { products: productConfirmations } : undefined
      }) as PurchaseOrder,
    [
      purchaseOrderId,
      completionStatus,
      asn,
      assignedWarehouseCompany,
      checkInStatus,
      checkInTimestamps,
      checkInFlaggedIssues,
      products,
      productConfirmations
    ]
  );

  if (
    !asn &&
    !assignedWarehouseCompany &&
    checkInNotes.length === 0 &&
    (!childPurchaseOrderIds || childPurchaseOrderIds.length === 0)
  ) {
    return null;
  }

  const hasParentContent = !!(asn || assignedWarehouseCompany || products.length > 0);

  return (
    <div className="bg-white border border-secondary-200 rounded-xl shadow-soft overflow-hidden">
      <CollapsibleSection
        initiallyOpen={false}
        title={
          <h3 className="text-lg font-semibold text-secondary-800 flex items-center">
            <Warehouse className="w-5 h-5 mr-2 text-primary-600" />
            Warehouse & Check-In
          </h3>
        }
      >
        <div className="p-6 space-y-8">
          {hasParentContent && showProductReceipts && (
            <div>
              <SinglePOWarehouseContent
                purchaseOrder={parentPurchaseOrder}
                isChild={false}
                childReceivedQuantitiesMap={childReceivedQuantitiesMap}
                hasChildPOs={hasChildPOs}
              />
            </div>
          )}

          <CheckInNotesPanel notes={checkInNotes} />

          {isLoadingChildPOs && (
            <div className="py-8">
              <LoadingState type="LOADING" />
            </div>
          )}

          {childPurchaseOrders.length > 0 && (
            <div className="space-y-4">
              <h4 className="text-base font-semibold text-secondary-800">Child Purchase Orders</h4>
              {childPurchaseOrders.map((childPO) => (
                <div
                  key={childPO.id}
                  className="bg-white border border-secondary-200 rounded-xl shadow-soft overflow-hidden"
                >
                  <CollapsibleSection
                    initiallyOpen={false}
                    title={
                      <div className="flex items-center justify-between w-full">
                        <div className="flex items-center gap-3">
                          <Warehouse className="w-4 h-4 text-primary-600" />
                          <Link
                            to={`${ROUTES_CONFIG.purchaseOrders.path}?id=${childPO.id}`}
                            className="text-sm font-semibold text-primary-700 hover:text-primary-800 transition-colors"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {childPO.id}
                          </Link>
                        </div>
                        {childPO.completionStatus && (
                          <div className="ml-3">
                            <StatusPill status={childPO.completionStatus} />
                          </div>
                        )}
                      </div>
                    }
                  >
                    <div className="p-6">
                      <SinglePOWarehouseContent purchaseOrder={childPO} isChild={true} hasChildPOs={false} />
                    </div>
                  </CollapsibleSection>
                </div>
              ))}
            </div>
          )}
        </div>
      </CollapsibleSection>
    </div>
  );
};
